import React from 'react';
import {
  Database,
  Brain,
  Workflow,
  Lock,
  Cpu,
  Server,
  Shield,
  Zap,
  BarChart3,
  Layers,
  GitBranch,
  Gauge,
  Cloud,
  Wifi,
  AlertTriangle,
  BookOpen,
  Search,
  MessageSquare,
  Boxes,
  Activity,
  TrendingUp,
  FileCheck,
  Globe,
  HardDrive,
  Radio,
} from 'lucide-react';
import type { SolutionItem, SolutionDomainId } from './types';

export const SOLUTION_DOMAINS: { id: SolutionDomainId; label: string; path: string }[] = [
  { id: 'ai', label: 'AI', path: '/solutions?domain=ai' },
  { id: 'data', label: 'Data', path: '/solutions?domain=data' },
  { id: 'cloud', label: 'Cloud Infrastructure', path: '/solutions?domain=cloud' },
  { id: 'sre', label: 'Site Reliability', path: '/solutions?domain=sre' },
  { id: 'cyber', label: 'Cybersecurity', path: '/solutions?domain=cyber' },
];

const useIn = (
  ai: { heading: string; points: string[] },
  data: { heading: string; points: string[] },
  cloud: { heading: string; points: string[] },
  sre: { heading: string; points: string[] },
  cyber: { heading: string; points: string[] }
) => [
  { domainId: 'ai' as SolutionDomainId, ...ai },
  { domainId: 'data' as SolutionDomainId, ...data },
  { domainId: 'cloud' as SolutionDomainId, ...cloud },
  { domainId: 'sre' as SolutionDomainId, ...sre },
  { domainId: 'cyber' as SolutionDomainId, ...cyber },
];

export const SOLUTIONS: SolutionItem[] = [
  {
    id: 'data-platform',
    slug: 'ai-ready-data-platform',
    title: 'AI-Ready Data Platform',
    description: 'Unified lakehouse architecture for high-throughput AI workloads. Bridge transactional data and model inference.',
    outcome: '70% faster data prep',
    domains: ['data', 'ai'],
    keywords: ['lakehouse', 'batch', 'streaming', 'vector'],
    icon: <Database className="w-6 h-6" />,
    features: ['Batch & streaming ingestion', 'Automated lineage', 'Vector DB integration'],
    detail: {
      intro: 'A single platform that serves both analytics and AI. We design lakehouse and data mesh patterns so your models get timely, governed data—with one source of truth, clear lineage, and the right latency for training and inference.',
      techStack: [
        { category: 'Ingestion & orchestration', items: ['Apache Kafka', 'Fivetran / Airbyte', 'dbt Core', 'Apache Airflow'] },
        { category: 'Storage & format', items: ['Delta Lake', 'Apache Iceberg', 'AWS S3 / GCS / ADLS'] },
        { category: 'Compute & query', items: ['Databricks', 'Apache Spark', 'Trino / Presto'] },
        { category: 'Catalog & governance', items: ['Unity Catalog', 'Apache Atlas', 'OpenLineage'] },
        { category: 'Vector & ML', items: ['Feast', 'Pinecone / Weaviate', 'MLflow'] },
      ],
      predictedImpact: [
        { metric: 'Time to insight', value: '50–70% faster', description: 'Unified pipelines and catalog reduce duplicate work and wait times for data prep.' },
        { metric: 'Data duplication', value: 'Up to 60% reduction', description: 'Single lakehouse replaces siloed data marts and copy-paste pipelines.' },
        { metric: 'Compliance readiness', value: 'Audit-ready', description: 'Lineage and access controls built in for GDPR and internal audit.' },
        { metric: 'TCO vs legacy', value: '20–40% lower', description: 'Managed services and elasticity reduce fixed cost and over-provisioning.' },
      ],
      useCases: [
        {
          title: 'Unified analytics and ML training in financial services',
          domainId: 'data',
          scenario: 'A European bank runs BI on a legacy EDW and trains fraud models in a separate data science environment. Data is copied between systems, leading to latency, inconsistency, and compliance risk. They need one platform that serves both reporting and ML while meeting strict regulatory requirements.',
          steps: [
            'Design a lakehouse on Delta Lake with bronze/silver/gold layers and clear ownership per domain (transactions, customers, risk).',
            'Migrate critical EDW tables via CDC and batch backfill; establish dbt models for silver/gold and expose them to both BI tools and ML feature jobs.',
            'Implement Unity Catalog (or equivalent) for column- and row-level access, PII tagging, and full lineage from source to report and model.',
            'Connect the feature store (Feast) to the gold layer so training and real-time inference use the same definitions; set SLOs for pipeline freshness and quality checks.',
          ],
          outcome: 'BI and ML share one governed source of truth. Data prep time for new models dropped by ~65%; audit requests are answered from lineage in hours instead of weeks. The bank can ship new fraud signals to production without copying data or bypassing governance.',
        },
        {
          title: 'Real-time feature pipeline for recommendation at scale',
          domainId: 'ai',
          scenario: 'An e‑commerce company wants to move from batch-only recommendations to real-time personalisation. Current batch jobs run nightly; they need streaming events (clicks, cart, search) and inventory updates to drive low-latency ranking within 100–200 ms.',
          steps: [
            'Ingest clickstream and catalog events via Kafka; land raw events in the lakehouse and stream aggregations (e.g. session counts, recent items) into a key–value store for online serving.',
            'Build a feature pipeline that computes batch features (e.g. 30-day co-views) in Spark and incremental/session features in a stream processor; register both in the feature store with clear TTLs and backfill support.',
            'Serve features to the ranking service via a low-latency API; monitor freshness and drift and set alerts when batch or stream pipelines fall behind.',
          ],
          outcome: 'Recommendations use up-to-date behaviour and inventory. Click-through and conversion metrics improved by double digits in A/B tests; the platform supports adding new real-time signals without redesigning the whole pipeline.',
        },
      ],
      useInDomains: useIn(
        { heading: 'AI', points: ['Training and inference pipelines', 'Feature store integration', 'Model-ready datasets'] },
        { heading: 'Data', points: ['Lakehouse and data mesh', 'Real-time and batch ETL', 'Quality and lineage'] },
        { heading: 'Cloud Infrastructure', points: ['Managed data services', 'Cross-region sync', 'Cost-aware storage tiers'] },
        { heading: 'Site Reliability', points: ['Pipeline monitoring', 'SLA for data freshness', 'Incident runbooks'] },
        { heading: 'Cybersecurity', points: ['Data access and lineage', 'Encryption at rest and in transit', 'Audit trails'] }
      ),
      ctaLine: 'Ready to modernise your data foundation for AI?',
    },
  },
  {
    id: 'enterprise-intel',
    slug: 'enterprise-intelligence',
    title: 'Enterprise Intelligence',
    description: 'Centralised knowledge management. Ingest documents (PDFs, wikis, email) and make them queryable via semantic search.',
    outcome: 'Instant knowledge access',
    domains: ['ai', 'data'],
    keywords: ['RAG', 'semantic search', 'documents', 'knowledge'],
    icon: <Brain className="w-6 h-6" />,
    features: ['RAG pipelines', 'Semantic search API', 'Document parsing'],
    detail: {
      intro: 'Turn scattered documents and wikis into a searchable, cited knowledge base. Semantic search and RAG so teams find answers in context with traceable sources.',
      techStack: [
        { category: 'Ingestion & parsing', items: ['Unstructured.io', 'Apache Tika', 'LlamaParse', 'Custom PDF/Office parsers'] },
        { category: 'Embeddings & vector', items: ['OpenAI / Cohere / open-source embed models', 'Pinecone', 'Weaviate', 'pgvector'] },
        { category: 'Retrieval & ranking', items: ['LangChain / LlamaIndex', 'Cross-encoder rerankers', 'Hybrid search (keyword + vector)'] },
        { category: 'Serving & API', items: ['FastAPI', 'LangServe', 'Caching (Redis)'] },
      ],
      predictedImpact: [
        { metric: 'Time to find answers', value: '60–80% faster', description: 'Semantic search and RAG surface the right doc in seconds instead of manual browsing.' },
        { metric: 'Citation & trust', value: 'Traceable', description: 'Every answer links to source chunks so users and auditors can verify.' },
        { metric: 'Coverage', value: 'All docs searchable', description: 'Ingest PDFs, wikis, and email so no knowledge stays in silos.' },
      ],
      useCases: [
        {
          title: 'Internal knowledge assistant for support and sales',
          domainId: 'ai',
          scenario: 'A B2B SaaS company has thousands of pages of product docs, release notes, and internal playbooks. Support and sales spend hours searching Confluence and shared drives. They need a single assistant that answers from all sources and cites where the answer came from.',
          steps: [
            'Ingest Confluence, Google Drive, and Notion via connectors; parse PDFs and Office docs and chunk with overlap for retrieval.',
            'Embed chunks with a chosen model (e.g. OpenAI or open-source) and index in a vector store; add metadata (product, version, team) for filtering.',
            'Build a RAG API: retrieve top-k chunks, rerank with a cross-encoder, and generate an answer with in-line citations; enforce PII and access rules.',
            'Expose via chat UI and Slack/Teams; monitor latency, citation quality, and user feedback.',
          ],
          outcome: 'Support and sales get accurate, cited answers in seconds. Time spent searching dropped by ~70%; new hires ramp faster because the assistant explains policies and product behaviour with sources.',
        },
      ],
      useInDomains: useIn(
        { heading: 'AI', points: ['RAG and retrieval', 'Embeddings and ranking', 'Citation and grounding'] },
        { heading: 'Data', points: ['Document ingestion', 'Metadata and taxonomy', 'Access control'] },
        { heading: 'Cloud Infrastructure', points: ['Index and vector store', 'Scaling search', 'Caching'] },
        { heading: 'Site Reliability', points: ['Search latency SLOs', 'Index build pipelines', 'Fallbacks'] },
        { heading: 'Cybersecurity', points: ['Access control on docs', 'PII and redaction', 'Audit logs'] }
      ),
      ctaLine: 'Want enterprise search that understands context?',
    },
  },
  {
    id: 'genai-factory',
    slug: 'genai-automation-factory',
    title: 'GenAI Automation Factory',
    description: 'Standardised framework for building, testing, and deploying AI agents for specific business processes.',
    outcome: 'Scale to 50+ agents',
    domains: ['ai'],
    keywords: ['agents', 'copilots', 'orchestration', 'prompt'],
    icon: <Workflow className="w-6 h-6" />,
    features: ['Agent orchestration', 'Prompt versioning', 'Eval frameworks'],
    detail: {
      intro: 'From one-off copilots to a portfolio of agents. We help you standardise tool use, evaluation, and deployment so GenAI scales safely.',
      techStack: [
        { category: 'Orchestration', items: ['LangGraph', 'CrewAI', 'AutoGen', 'Custom state machines'] },
        { category: 'LLM & inference', items: ['OpenAI', 'Anthropic', 'Open-source (Llama, Mistral)', 'vLLM / TGI'] },
        { category: 'Tools & eval', items: ['LangSmith', 'Weights & Biases', 'Custom evals', 'Prompt versioning (Arize, etc.)'] },
        { category: 'Serving & ops', items: ['FastAPI', 'LangServe', 'Redis / queue', 'Observability'] },
      ],
      predictedImpact: [
        { metric: 'Agent rollout', value: '50+ agents', description: 'Standardised framework lets you ship many agents without reinventing the stack.' },
        { metric: 'Time to production', value: '40–60% faster', description: 'Shared orchestration, evals, and tool patterns reduce per-agent build time.' },
        { metric: 'Safety & compliance', value: 'Audit-ready', description: 'Centralised logging, eval gates, and human-in-the-loop for high-stakes flows.' },
      ],
      useCases: [
        {
          title: 'Portfolio of internal copilots (support, sales, ops)',
          domainId: 'ai',
          scenario: 'A mid-size enterprise has built three separate GenAI copilots (support, sales, ops) with different stacks and no shared eval or governance. They want to scale to 20+ use cases without each team rebuilding from scratch, while keeping consistent safety and audit trails.',
          steps: [
            'Define a shared agent framework: state machine, tool registry, and standard hooks for logging and human-in-the-loop.',
            'Migrate existing copilots onto the framework; unify prompt versioning and eval (e.g. LangSmith or internal) so every agent runs the same regression and safety checks.',
            'Establish a catalog of reusable tools (CRM, ticketing, docs) and guardrails (PII redaction, output filters) so new agents compose instead of custom code.',
            'Set SLOs per agent (latency, availability, cost) and centralised on-call with runbooks for model and tool failures.',
          ],
          outcome: 'The company ships new copilots in weeks instead of months. Evals and guardrails are consistent; auditors get a single view of agent behaviour and data access.',
        },
      ],
      useInDomains: useIn(
        { heading: 'AI', points: ['Multi-step agents', 'Tool and API use', 'Evaluation and safety'] },
        { heading: 'Data', points: ['Agent context and memory', 'Audit logs', 'Data access policies'] },
        { heading: 'Cloud Infrastructure', points: ['Inference and routing', 'Rate limits', 'Multi-region'] },
        { heading: 'Site Reliability', points: ['Agent availability', 'Fallback and retries', 'On-call for agents'] },
        { heading: 'Cybersecurity', points: ['Agent tool access control', 'Audit and guardrails', 'Compliance logging'] }
      ),
      ctaLine: 'Ready to scale from one agent to many?',
    },
  },
  {
    id: 'sovereign-ai',
    slug: 'sovereign-ai-infrastructure',
    title: 'Sovereign AI Infrastructure',
    description: 'Deploy open-weights models (Llama, Mistral) in your VPC or on-prem. Full data sovereignty.',
    outcome: 'Zero data leakage',
    domains: ['ai', 'cloud', 'cyber'],
    keywords: ['private', 'VPC', 'fine-tuning', 'air-gap'],
    icon: <Lock className="w-6 h-6" />,
    features: ['Air-gapped deployment', 'Custom fine-tuning', 'RBAC & audit logs'],
    detail: {
      intro: 'Keep model and data inside your perimeter. We design and operate private inference and fine-tuning so you meet sovereignty and compliance.',
      techStack: [
        { category: 'Models & serving', items: ['vLLM', 'TGI', 'Llama.cpp', 'SGLang'] },
        { category: 'Fine-tuning', items: ['LoRA / QLoRA', 'Unsloth', 'Axolotl', 'Custom adapters'] },
        { category: 'Infrastructure', items: ['Kubernetes', 'NVIDIA GPU operator', 'VPC / on-prem'] },
        { category: 'Governance', items: ['RBAC', 'Audit logs', 'Model registry'] },
      ],
      predictedImpact: [
        { metric: 'Data sovereignty', value: 'Zero egress', description: 'Model and data stay in your VPC or on-prem; no data sent to third-party APIs.' },
        { metric: 'Compliance', value: 'Audit-ready', description: 'Access, training data, and inference are logged for regulators and internal audit.' },
        { metric: 'Cost at scale', value: 'Predictable', description: 'No per-token vendor lock-in; capacity is under your control.' },
      ],
      useCases: [
        {
          title: 'Private LLM for regulated financial documents',
          domainId: 'ai',
          scenario: 'A bank needs to summarise and classify sensitive client documents using LLMs. Public APIs are prohibited by policy; they need inference and optional fine-tuning entirely within their VPC, with full audit trails and access control.',
          steps: [
            'Deploy an open-weights model (e.g. Llama, Mistral) with vLLM or TGI in the bank’s Kubernetes cluster; restrict network egress so no data leaves the VPC.',
            'Implement RBAC and audit logging for model access, inference requests, and (if used) fine-tuning jobs; integrate with existing IAM and SIEM.',
            'Optionally fine-tune on internal document types with LoRA/adapters; version models in a registry and promote via approval workflow.',
            'Set SLOs for latency and availability; plan capacity and failover so critical workflows are not dependent on a single region.',
          ],
          outcome: 'The bank runs summarisation and classification at scale with zero data leaving the perimeter. Auditors can trace every inference and model change; the team can iterate on models without policy exceptions.',
        },
      ],
      useInDomains: useIn(
        { heading: 'AI', points: ['Private inference', 'Fine-tuning and adapters', 'Model versioning'] },
        { heading: 'Data', points: ['In-perimeter storage', 'Access and retention', 'Lineage'] },
        { heading: 'Cloud Infrastructure', points: ['VPC and networking', 'GPU and scaling', 'Hybrid and on-prem'] },
        { heading: 'Site Reliability', points: ['Uptime and failover', 'Capacity planning', 'Security patches'] },
        { heading: 'Cybersecurity', points: ['In-perimeter inference', 'RBAC and audit', 'Zero data egress'] }
      ),
      ctaLine: 'Need AI that never leaves your perimeter?',
    },
  },
  {
    id: 'feature-store',
    slug: 'real-time-feature-store',
    title: 'Real-Time Feature Store',
    description: 'Low-latency feature serving for training and inference. Consistent features across batch and online.',
    outcome: 'Single source of features',
    domains: ['data', 'ai'],
    keywords: ['features', 'training', 'inference', 'low-latency'],
    icon: <Layers className="w-6 h-6" />,
    features: ['Batch and online serving', 'Point-in-time correctness', 'Governance'],
    detail: {
      intro: 'One feature store for training and inference. We implement point-in-time correctness, backfills, and governance so models stay consistent.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Training and inference', 'Feature reuse', 'A/B and experiments'] },
        { heading: 'Data', points: ['Feature pipelines', 'Lineage', 'Quality checks'] },
        { heading: 'Cloud Infrastructure', points: ['Serving tier', 'Caching', 'Scaling'] },
        { heading: 'Site Reliability', points: ['Latency SLOs', 'Freshness monitoring', 'Incidents'] },
        { heading: 'Cybersecurity', points: ['Feature access control', 'PII in features', 'Audit for training data'] }
      ),
      ctaLine: 'Want one feature layer for all models?',
    },
  },
  {
    id: 'data-lakehouse',
    slug: 'data-lakehouse',
    title: 'Data Lakehouse',
    description: 'Unified storage and compute for analytics and AI. Delta/Iceberg with managed compute.',
    outcome: 'One platform for BI and AI',
    domains: ['data'],
    keywords: ['lakehouse', 'delta', 'iceberg', 'warehouse'],
    icon: <Database className="w-6 h-6" />,
    features: ['Delta or Iceberg', 'ACID and time travel', 'BI and ML workloads'],
    detail: {
      intro: 'Replace siloed data lake and warehouse with a single lakehouse. We design schema, partitioning, and access so BI and AI share one source.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Training data', 'Feature backfills', 'Model artifacts'] },
        { heading: 'Data', points: ['Tables and schemas', 'ETL and ingestion', 'Governance'] },
        { heading: 'Cloud Infrastructure', points: ['Object store', 'Compute engines', 'Cost control'] },
        { heading: 'Site Reliability', points: ['Pipeline SLAs', 'Backup', 'Disaster recovery'] },
        { heading: 'Cybersecurity', points: ['Table and column access', 'Encryption', 'Lineage for audit'] }
      ),
      ctaLine: 'Ready to consolidate lake and warehouse?',
    },
  },
  {
    id: 'streaming-pipelines',
    slug: 'streaming-pipelines',
    title: 'Streaming Pipelines',
    description: 'Real-time ingestion and processing. Kafka/Flink-style pipelines for events and CDC.',
    outcome: 'Sub-second data freshness',
    domains: ['data', 'cloud'],
    keywords: ['streaming', 'Kafka', 'CDC', 'real-time'],
    icon: <Activity className="w-6 h-6" />,
    features: ['Event streaming', 'CDC and change data', 'Exactly-once semantics'],
    detail: {
      intro: 'From batch to real time. We build and operate streaming pipelines so your analytics and AI see data as it happens.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Real-time features', 'Online learning', 'Inference triggers'] },
        { heading: 'Data', points: ['Event ingestion', 'Stream processing', 'Sinks and APIs'] },
        { heading: 'Cloud Infrastructure', points: ['Kafka/Managed Kafka', 'Connectors', 'Scaling'] },
        { heading: 'Site Reliability', points: ['Lag and throughput', 'Backpressure', 'Failover'] },
        { heading: 'Cybersecurity', points: ['Event access control', 'Encryption in transit', 'Audit for streams'] }
      ),
      ctaLine: 'Need real-time data for analytics and AI?',
    },
  },
  {
    id: 'data-quality-lineage',
    slug: 'data-quality-lineage',
    title: 'Data Quality & Lineage',
    description: 'Automated quality checks and end-to-end lineage. Trust and traceability for compliance.',
    outcome: 'Trusted, traceable data',
    domains: ['data'],
    keywords: ['quality', 'lineage', 'governance', 'compliance'],
    icon: <FileCheck className="w-6 h-6" />,
    features: ['Automated quality rules', 'Column and table lineage', 'Catalog integration'],
    detail: {
      intro: 'Know where data came from and how good it is. We implement rules, lineage, and catalog so you can trust and explain every dataset.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Training data quality', 'Model lineage', 'Bias and fairness'] },
        { heading: 'Data', points: ['Rules and monitoring', 'Lineage graphs', 'Ownership'] },
        { heading: 'Cloud Infrastructure', points: ['Scan and compute', 'Storage', 'APIs'] },
        { heading: 'Site Reliability', points: ['Quality SLOs', 'Alerts', 'Runbooks'] },
        { heading: 'Cybersecurity', points: ['Lineage for compliance', 'PII tagging', 'Access policies'] }
      ),
      ctaLine: 'Want lineage and quality built in?',
    },
  },
  {
    id: 'vector-search',
    slug: 'vector-search-embeddings',
    title: 'Vector Search & Embeddings',
    description: 'Semantic search at scale. Embedding pipelines and vector indexes for RAG and discovery.',
    outcome: 'Find by meaning, not keywords',
    domains: ['ai', 'data'],
    keywords: ['vector', 'embedding', 'semantic', 'search'],
    icon: <Search className="w-6 h-6" />,
    features: ['Embedding pipelines', 'Vector index (e.g. Pinecone/Weaviate)', 'Hybrid search'],
    detail: {
      intro: 'From keyword to semantic. We build embedding pipelines and vector search so RAG and discovery work at your scale.',
      useInDomains: useIn(
        { heading: 'AI', points: ['RAG retrieval', 'Recommendations', 'Deduplication'] },
        { heading: 'Data', points: ['Embedding jobs', 'Metadata', 'Access control'] },
        { heading: 'Cloud Infrastructure', points: ['Vector DB', 'Index builds', 'Caching'] },
        { heading: 'Site Reliability', points: ['Search latency', 'Index freshness', 'Capacity'] },
        { heading: 'Cybersecurity', points: ['Vector store access', 'Query audit', 'PII in embeddings'] }
      ),
      ctaLine: 'Ready for semantic search at scale?',
    },
  },
  {
    id: 'mlops-pipeline',
    slug: 'mlops-pipeline',
    title: 'MLOps Pipeline',
    description: 'CI/CD for ML. Versioned experiments, model registry, and automated deployment.',
    outcome: 'Ship models like software',
    domains: ['ai', 'sre'],
    keywords: ['MLOps', 'CI/CD', 'registry', 'deployment'],
    icon: <GitBranch className="w-6 h-6" />,
    features: ['Experiment tracking', 'Model registry', 'Staged rollout'],
    detail: {
      intro: 'From experiment to production. We set up versioning, registry, and deployment pipelines so ML ships with the same discipline as code.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Experiments and models', 'Evaluation gates', 'Rollback'] },
        { heading: 'Data', points: ['Data versioning', 'Training pipelines', 'Feature dependencies'] },
        { heading: 'Cloud Infrastructure', points: ['Build and deploy', 'Environments', 'Secrets'] },
        { heading: 'Site Reliability', points: ['Release process', 'Canary and rollout', 'Incidents'] },
        { heading: 'Cybersecurity', points: ['Model registry access', 'Pipeline secrets', 'Audit for deploys'] }
      ),
      ctaLine: 'Need CI/CD that understands ML?',
    },
  },
  {
    id: 'model-serving',
    slug: 'model-serving-scale',
    title: 'Model Serving at Scale',
    description: 'Low-latency inference API. Autoscaling, batching, and guardrails in production.',
    outcome: 'Millions of predictions, stable latency',
    domains: ['ai', 'cloud', 'sre'],
    keywords: ['inference', 'API', 'latency', 'GPU'],
    icon: <Cpu className="w-6 h-6" />,
    features: ['REST and gRPC', 'Batching and autoscaling', 'Guardrails and logging'],
    detail: {
      intro: 'Serve models in production with predictable latency and cost. We design APIs, scaling, and guardrails so inference is reliable and auditable.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Inference stack', 'Batching', 'Model variants'] },
        { heading: 'Data', points: ['Input validation', 'Logging', 'Audit'] },
        { heading: 'Cloud Infrastructure', points: ['GPU/CPU scaling', 'Load balancing', 'Regions'] },
        { heading: 'Site Reliability', points: ['Latency SLOs', 'Error budgets', 'On-call'] },
        { heading: 'Cybersecurity', points: ['API auth and RBAC', 'Input validation', 'Inference audit'] }
      ),
      ctaLine: 'Ready to serve models at scale?',
    },
  },
  {
    id: 'rag-assistants',
    slug: 'rag-knowledge-assistants',
    title: 'RAG & Knowledge Assistants',
    description: 'Question-answering over your docs. RAG with citations and guardrails.',
    outcome: 'Accurate answers with sources',
    domains: ['ai'],
    keywords: ['RAG', 'QA', 'citations', 'assistant'],
    icon: <BookOpen className="w-6 h-6" />,
    features: ['Retrieval and rerank', 'Citation and grounding', 'Guardrails'],
    detail: {
      intro: 'Let users ask questions and get cited answers. We build RAG systems with retrieval, rerank, and guardrails so answers are accurate and traceable.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Retrieval', 'Prompting', 'Evaluation'] },
        { heading: 'Data', points: ['Source docs', 'Metadata', 'Access'] },
        { heading: 'Cloud Infrastructure', points: ['Inference', 'Vector store', 'Caching'] },
        { heading: 'Site Reliability', points: ['Availability', 'Latency', 'Fallbacks'] },
        { heading: 'Cybersecurity', points: ['Source access control', 'Citation audit', 'PII in context'] }
      ),
      ctaLine: 'Want RAG that cites and stays on topic?',
    },
  },
  {
    id: 'copilots-agents',
    slug: 'copilots-agents',
    title: 'Copilots & Agents',
    description: 'Assistants that use tools and APIs. Multi-step workflows with human-in-the-loop.',
    outcome: 'AI that acts, not just answers',
    domains: ['ai'],
    keywords: ['copilot', 'agent', 'tools', 'workflow'],
    icon: <MessageSquare className="w-6 h-6" />,
    features: ['Tool use', 'Multi-step plans', 'Human-in-the-loop'],
    detail: {
      intro: 'From chat to action. We design copilots and agents that call tools and APIs, with clear boundaries and human oversight where needed.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Tool calling', 'Planning', 'Safety'] },
        { heading: 'Data', points: ['Context', 'Audit', 'PII'] },
        { heading: 'Cloud Infrastructure', points: ['Orchestration', 'APIs', 'Rate limits'] },
        { heading: 'Site Reliability', points: ['Agent uptime', 'Timeouts', 'Escalation'] },
        { heading: 'Cybersecurity', points: ['Tool access control', 'Agent audit trail', 'PII and guardrails'] }
      ),
      ctaLine: 'Ready for AI that uses your systems?',
    },
  },
  {
    id: 'private-llms',
    slug: 'private-llms-finetuning',
    title: 'Private LLMs & Fine-Tuning',
    description: 'Run and adapt LLMs in your environment. Fine-tuning and inference on your data.',
    outcome: 'Your data, your model',
    domains: ['ai', 'cloud'],
    keywords: ['LLM', 'fine-tuning', 'private', 'adapters'],
    icon: <Brain className="w-6 h-6" />,
    features: ['On-prem or VPC inference', 'LoRA/adapters', 'Evaluation'],
    detail: {
      intro: 'Own the full stack. We help you run and fine-tune LLMs in your perimeter, with adapters and evaluation so models fit your domain.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Fine-tuning', 'Adapters', 'Eval'] },
        { heading: 'Data', points: ['Training data', 'Retention', 'Governance'] },
        { heading: 'Cloud Infrastructure', points: ['GPU', 'Storage', 'Networking'] },
        { heading: 'Site Reliability', points: ['Inference SLA', 'Updates', 'Capacity'] },
        { heading: 'Cybersecurity', points: ['In-perimeter only', 'RBAC and audit', 'Training data governance'] }
      ),
      ctaLine: 'Need LLMs that stay inside your walls?',
    },
  },
  {
    id: 'computer-vision',
    slug: 'computer-vision-pipeline',
    title: 'Computer Vision Pipeline',
    description: 'Image and video ML at scale. From ingestion to inference and feedback loops.',
    outcome: 'Vision that scales',
    domains: ['ai', 'data'],
    keywords: ['vision', 'image', 'video', 'detection'],
    icon: <Boxes className="w-6 h-6" />,
    features: ['Ingestion and labelling', 'Training and eval', 'Edge or cloud inference'],
    detail: {
      intro: 'From raw images to production vision. We build pipelines for labelling, training, and inference so vision models ship and improve over time.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Models', 'Eval', 'Edge inference'] },
        { heading: 'Data', points: ['Image store', 'Labels', 'Versioning'] },
        { heading: 'Cloud Infrastructure', points: ['GPU training', 'Serving', 'Storage'] },
        { heading: 'Site Reliability', points: ['Throughput', 'Latency', 'Drift'] },
        { heading: 'Cybersecurity', points: ['Image data access', 'Model access control', 'Audit for inference'] }
      ),
      ctaLine: 'Ready to ship vision at scale?',
    },
  },
  {
    id: 'recommendation-engine',
    slug: 'recommendation-engine',
    title: 'Recommendation Engine',
    description: 'Personalised recommendations. Real-time ranking and A/B testing.',
    outcome: 'Relevant results, measurable lift',
    domains: ['ai', 'data'],
    keywords: ['recommendation', 'ranking', 'personalisation', 'A/B'],
    icon: <TrendingUp className="w-6 h-6" />,
    features: ['Real-time ranking', 'Offline and online', 'A/B and metrics'],
    detail: {
      intro: 'Recommend what matters. We design ranking and retrieval so recommendations are fast, relevant, and measurable.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Models', 'Ranking', 'Diversity'] },
        { heading: 'Data', points: ['Signals', 'Features', 'Feedback'] },
        { heading: 'Cloud Infrastructure', points: ['Serving', 'Caching', 'Scale'] },
        { heading: 'Site Reliability', points: ['Latency', 'Fallbacks', 'Experiments'] },
        { heading: 'Cybersecurity', points: ['User data access', 'Recommendation audit', 'PII in signals'] }
      ),
      ctaLine: 'Want recommendations that convert?',
    },
  },
  {
    id: 'anomaly-detection',
    slug: 'anomaly-detection',
    title: 'Anomaly Detection',
    description: 'Detect outliers in streams and batches. Alerts and root-cause hints.',
    outcome: 'Catch issues before users do',
    domains: ['ai', 'data'],
    keywords: ['anomaly', 'outlier', 'alert', 'monitoring'],
    icon: <AlertTriangle className="w-6 h-6" />,
    features: ['Stream and batch', 'Thresholds and ML', 'Alerting'],
    detail: {
      intro: 'Spot the unexpected. We build anomaly detection for metrics, logs, and events so you get alerts with context, not noise.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Models', 'Unsupervised', 'Explainability'] },
        { heading: 'Data', points: ['Streams', 'Features', 'History'] },
        { heading: 'Cloud Infrastructure', points: ['Compute', 'Storage', 'APIs'] },
        { heading: 'Site Reliability', points: ['Alerts', 'SLOs', 'Runbooks'] },
        { heading: 'Cybersecurity', points: ['Anomaly data access', 'Alert audit', 'Sensitive data handling'] }
      ),
      ctaLine: 'Need smarter anomaly alerts?',
    },
  },
  {
    id: 'forecasting-planning',
    slug: 'forecasting-planning',
    title: 'Forecasting & Planning',
    description: 'Demand, capacity, and revenue forecasting. Production models with confidence intervals.',
    outcome: 'Plans backed by data',
    domains: ['data', 'ai'],
    keywords: ['forecast', 'demand', 'capacity', 'planning'],
    icon: <BarChart3 className="w-6 h-6" />,
    features: ['Demand and capacity', 'Uncertainty', 'Scenarios'],
    detail: {
      intro: 'Forecast with confidence. We build production forecasting for demand, capacity, and revenue so planning is data-driven and auditable.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Time series', 'Uncertainty', 'Ensembles'] },
        { heading: 'Data', points: ['History', 'Features', 'Backtesting'] },
        { heading: 'Cloud Infrastructure', points: ['Batch jobs', 'APIs', 'Storage'] },
        { heading: 'Site Reliability', points: ['Freshness', 'SLA', 'Alerts'] },
        { heading: 'Cybersecurity', points: ['Forecast data access', 'Model audit', 'Sensitive inputs'] }
      ),
      ctaLine: 'Ready for production forecasting?',
    },
  },
  {
    id: 'data-mesh',
    slug: 'multi-cloud-data-mesh',
    title: 'Multi-Cloud Data Mesh',
    description: 'Federated data ownership. Domains own their data; platform provides discoverability and governance.',
    outcome: 'Scale data without central bottleneck',
    domains: ['data', 'cloud'],
    keywords: ['data mesh', 'multi-cloud', 'federated', 'governance'],
    icon: <Globe className="w-6 h-6" />,
    features: ['Domain ownership', 'Catalog and discovery', 'Cross-cloud access'],
    detail: {
      intro: 'Data at scale without a single bottleneck. We help you adopt data mesh so domains own their data while platform provides discovery and governance.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Cross-domain features', 'Model access', 'Lineage'] },
        { heading: 'Data', points: ['Domains', 'Contracts', 'Catalog'] },
        { heading: 'Cloud Infrastructure', points: ['Multi-cloud', 'Networking', 'Identity'] },
        { heading: 'Site Reliability', points: ['SLA per domain', 'Incidents', 'Ownership'] },
        { heading: 'Cybersecurity', points: ['Domain access boundaries', 'Cross-domain audit', 'Data sharing policies'] }
      ),
      ctaLine: 'Want data mesh without the chaos?',
    },
  },
  {
    id: 'data-governance',
    slug: 'data-governance-catalog',
    title: 'Data Governance & Catalog',
    description: 'Discover, classify, and govern data. One catalog with policies and access.',
    outcome: 'Find and trust any dataset',
    domains: ['data', 'cyber'],
    keywords: ['governance', 'catalog', 'PII', 'compliance'],
    icon: <Shield className="w-6 h-6" />,
    features: ['Discovery and search', 'Classification and PII', 'Access policies'],
    detail: {
      intro: 'Know what you have and who can use it. We implement catalogs, classification, and policies so data is findable and compliant.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Training data', 'Bias', 'Audit'] },
        { heading: 'Data', points: ['Catalog', 'Lineage', 'Policies'] },
        { heading: 'Cloud Infrastructure', points: ['Scanning', 'Storage', 'APIs'] },
        { heading: 'Site Reliability', points: ['Catalog uptime', 'Sync', 'Alerts'] },
        { heading: 'Cybersecurity', points: ['Catalog access control', 'PII classification', 'Compliance and audit'] }
      ),
      ctaLine: 'Need a single source of truth for data?',
    },
  },
  {
    id: 'cloud-cost',
    slug: 'cloud-cost-optimization',
    title: 'Cloud Cost Optimization',
    description: 'Right-size and automate. Visibility, recommendations, and FinOps practices.',
    outcome: 'Lower cloud spend, no surprise bills',
    domains: ['cloud', 'sre'],
    keywords: ['cost', 'FinOps', 'optimization', 'budget'],
    icon: <TrendingUp className="w-6 h-6" />,
    features: ['Cost visibility', 'Recommendations', 'Budgets and alerts'],
    detail: {
      intro: 'Spend less without sacrificing performance. We bring visibility, rightsizing, and FinOps so cloud cost is predictable and optimised.',
      useInDomains: useIn(
        { heading: 'AI', points: ['GPU and inference cost', 'Training spend', 'Model cost'] },
        { heading: 'Data', points: ['Storage tiers', 'Query cost', 'Pipeline cost'] },
        { heading: 'Cloud Infrastructure', points: ['Reserved and spot', 'Auto-scaling', 'Tagging'] },
        { heading: 'Site Reliability', points: ['Cost vs SLO', 'Alerts', 'Chargeback'] },
        { heading: 'Cybersecurity', points: ['Cost visibility and governance', 'Anomaly and abuse detection', 'Budget alerts'] }
      ),
      ctaLine: 'Ready to tame cloud spend?',
    },
  },
  {
    id: 'kubernetes-scale',
    slug: 'kubernetes-at-scale',
    title: 'Kubernetes at Scale',
    description: 'Run workloads reliably. Cluster design, GitOps, and day-2 operations.',
    outcome: 'K8s that scales with you',
    domains: ['cloud', 'sre'],
    keywords: ['Kubernetes', 'K8s', 'GitOps', 'containers'],
    icon: <Server className="w-6 h-6" />,
    features: ['Cluster design', 'GitOps (Argo/Flux)', 'Scaling and upgrades'],
    detail: {
      intro: 'Kubernetes that stays manageable. We design clusters, GitOps, and operations so you scale without operational debt.',
      useInDomains: useIn(
        { heading: 'AI', points: ['GPU nodes', 'Inference workloads', 'Batch jobs'] },
        { heading: 'Data', points: ['Pipelines on K8s', 'Stateful sets', 'Storage'] },
        { heading: 'Cloud Infrastructure', points: ['EKS/GKE/AKS', 'Networking', 'Security'] },
        { heading: 'Site Reliability', points: ['Upgrades', 'Incidents', 'SLOs'] },
        { heading: 'Cybersecurity', points: ['Pod and namespace isolation', 'Secrets management', 'Network policies'] }
      ),
      ctaLine: 'Need K8s that your team can own?',
    },
  },
  {
    id: 'observability-apm',
    slug: 'observability-apm',
    title: 'Observability & APM',
    description: 'Traces, metrics, logs. One place to debug and understand system behaviour.',
    outcome: 'See the full picture',
    domains: ['sre', 'cloud'],
    keywords: ['observability', 'APM', 'traces', 'logs'],
    icon: <Gauge className="w-6 h-6" />,
    features: ['Distributed tracing', 'Metrics and dashboards', 'Log aggregation'],
    detail: {
      intro: 'From blind spots to full visibility. We implement tracing, metrics, and logs so you can debug and improve reliability with confidence.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Model latency', 'Inference traces', 'Drift metrics'] },
        { heading: 'Data', points: ['Pipeline metrics', 'Data freshness', 'Quality metrics'] },
        { heading: 'Cloud Infrastructure', points: ['Infra metrics', 'Cost', 'Capacity'] },
        { heading: 'Site Reliability', points: ['SLOs', 'Incidents', 'On-call'] },
        { heading: 'Cybersecurity', points: ['Trace and log access', 'PII in observability', 'Audit for access'] }
      ),
      ctaLine: 'Ready for observability that scales?',
    },
  },
  {
    id: 'disaster-recovery',
    slug: 'disaster-recovery-backup',
    title: 'Disaster Recovery & Backup',
    description: 'Backup, restore, and failover. RTO and RPO that match your risk appetite.',
    outcome: 'Recover with confidence',
    domains: ['cloud', 'sre'],
    keywords: ['DR', 'backup', 'failover', 'RTO'],
    icon: <HardDrive className="w-6 h-6" />,
    features: ['Backup strategy', 'Restore testing', 'Failover runbooks'],
    detail: {
      intro: 'When things go wrong, recover fast. We design backup and DR so RTO and RPO match your business needs and you test regularly.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Model and data backup', 'Inference failover', 'Training recovery'] },
        { heading: 'Data', points: ['DB and object backup', 'Replication', 'Point-in-time'] },
        { heading: 'Cloud Infrastructure', points: ['Multi-region', 'Backup storage', 'Networking'] },
        { heading: 'Site Reliability', points: ['RTO/RPO', 'Runbooks', 'Drills'] },
        { heading: 'Cybersecurity', points: ['Backup encryption', 'Restore access control', 'DR audit'] }
      ),
      ctaLine: 'Need DR that actually works?',
    },
  },
  {
    id: 'zero-trust',
    slug: 'zero-trust-security',
    title: 'Zero-Trust Security',
    description: 'Identity and access at the core. Least privilege, encryption, and audit.',
    outcome: 'Secure by design',
    domains: ['cloud', 'sre', 'cyber'],
    keywords: ['zero-trust', 'identity', 'RBAC', 'encryption'],
    icon: <Lock className="w-6 h-6" />,
    features: ['Identity and RBAC', 'Encryption in transit/rest', 'Audit logging'],
    detail: {
      intro: 'Assume breach, verify every request. We help you adopt zero-trust so access is explicit, auditable, and minimal.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Model access', 'Data access', 'API auth'] },
        { heading: 'Data', points: ['Access policies', 'PII', 'Encryption'] },
        { heading: 'Cloud Infrastructure', points: ['IAM', 'Networking', 'Secrets'] },
        { heading: 'Site Reliability', points: ['Incident access', 'Audit', 'Compliance'] },
        { heading: 'Cybersecurity', points: ['Zero-trust identity', 'Least privilege', 'Encryption and audit'] }
      ),
      ctaLine: 'Ready to harden with zero-trust?',
    },
  },
  {
    id: 'edge-inference',
    slug: 'edge-inference',
    title: 'Edge Inference',
    description: 'Run models at the edge. Low latency and offline capability where it matters.',
    outcome: 'Inference where data is',
    domains: ['ai', 'cloud'],
    keywords: ['edge', 'inference', 'latency', 'offline'],
    icon: <Radio className="w-6 h-6" />,
    features: ['Edge deployment', 'Model optimisation', 'Sync and fallback'],
    detail: {
      intro: 'Bring inference to the edge when latency or connectivity demands it. We design edge deployment and sync so models work offline and fast.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Edge models', 'Quantisation', 'Update strategy'] },
        { heading: 'Data', points: ['Edge data', 'Sync', 'Privacy'] },
        { heading: 'Cloud Infrastructure', points: ['Edge runtimes', 'OTA', 'Fleet'] },
        { heading: 'Site Reliability', points: ['Edge health', 'Rollout', 'Fallback'] },
        { heading: 'Cybersecurity', points: ['Edge device attestation', 'OTA and model signing', 'Local data only'] }
      ),
      ctaLine: 'Need inference at the edge?',
    },
  },
  {
    id: 'hybrid-connectivity',
    slug: 'hybrid-cloud-connectivity',
    title: 'Hybrid Cloud Connectivity',
    description: 'Secure connectivity between on-prem, cloud, and SaaS. Network and identity.',
    outcome: 'One logical network',
    domains: ['cloud'],
    keywords: ['hybrid', 'VPN', 'connectivity', 'on-prem'],
    icon: <Wifi className="w-6 h-6" />,
    features: ['VPN and private link', 'Identity federation', 'Traffic and policy'],
    detail: {
      intro: 'Connect on-prem and cloud without exposing the crown jewels. We design networking and identity so hybrid feels like one environment.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Model sync', 'Data flow', 'Inference hybrid'] },
        { heading: 'Data', points: ['Replication', 'Sync', 'Access'] },
        { heading: 'Cloud Infrastructure', points: ['Networking', 'Peering', 'DNS'] },
        { heading: 'Site Reliability', points: ['Connectivity SLA', 'Failover', 'Monitoring'] },
        { heading: 'Cybersecurity', points: ['Secure peering', 'Identity federation', 'Traffic encryption'] }
      ),
      ctaLine: 'Ready to connect hybrid safely?',
    },
  },
  {
    id: 'incident-oncall',
    slug: 'incident-response-oncall',
    title: 'Incident Response & On-Call',
    description: 'Structured incident management. On-call, escalation, and post-mortems.',
    outcome: 'Faster resolution, fewer repeats',
    domains: ['sre'],
    keywords: ['incident', 'on-call', 'PagerDuty', 'post-mortem'],
    icon: <AlertTriangle className="w-6 h-6" />,
    features: ['On-call and escalation', 'Incident workflow', 'Post-mortems'],
    detail: {
      intro: 'When things break, fix them fast and learn. We help you set up on-call, escalation, and blameless post-mortems so incidents become rarer.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Model incidents', 'Inference', 'Data issues'] },
        { heading: 'Data', points: ['Pipeline incidents', 'Quality', 'Availability'] },
        { heading: 'Cloud Infrastructure', points: ['Infra incidents', 'Capacity', 'Outages'] },
        { heading: 'Site Reliability', points: ['On-call', 'SLOs', 'Runbooks'] },
        { heading: 'Cybersecurity', points: ['Incident access control', 'Post-mortem audit', 'Security incidents'] }
      ),
      ctaLine: 'Want incidents that don’t repeat?',
    },
  },
  {
    id: 'sla-slo',
    slug: 'sla-slo-management',
    title: 'SLA & SLO Management',
    description: 'Define, measure, and improve reliability. Error budgets and alerting.',
    outcome: 'Reliability you can measure',
    domains: ['sre'],
    keywords: ['SLA', 'SLO', 'error budget', 'reliability'],
    icon: <Zap className="w-6 h-6" />,
    features: ['SLO definition', 'Error budgets', 'Alerting and dashboards'],
    detail: {
      intro: 'Turn reliability into numbers. We help you define SLOs and error budgets so you know when to fix and when to ship.',
      useInDomains: useIn(
        { heading: 'AI', points: ['Model latency', 'Availability', 'Accuracy'] },
        { heading: 'Data', points: ['Freshness', 'Quality', 'Pipeline'] },
        { heading: 'Cloud Infrastructure', points: ['Uptime', 'Capacity', 'Cost'] },
        { heading: 'Site Reliability', points: ['SLOs', 'Budgets', 'Reviews'] },
        { heading: 'Cybersecurity', points: ['SLO and access audit', 'Security SLOs', 'Compliance reviews'] }
      ),
      ctaLine: 'Ready to manage reliability by the numbers?',
    },
  },
];
